import { medplum } from '@/config/medplum';
import { queryClient } from '@/config/queryClient';
import { CUSTOM_IDENTIFIER_SYSTEMS } from '@/features/shared/constants/fhir-systems';

export const APP_PERMISSIONS = {
	SUPERADMIN: 'superadmin',
	MANAGE_USERS: 'manage_users',
	MANAGE_ENTITIES: 'manage_entities',
	ADMIN_DASHBOARD: 'admin_dashboard',
	ENROLLMENT_ADMIN: 'enrollment_admin',
	PATIENT_ENROLLMENT: 'patient_enrollment',
	CARE_ADMIN: 'care_admin',
	PATIENT_CARE: 'patient_care',
	VIEW_DASHBOARD: 'view_dashboard',
	EDIT_TASKS: 'edit_tasks',
	VIEW_REPORTS: 'view_reports',
} as const;

export type AppPermission = (typeof APP_PERMISSIONS)[keyof typeof APP_PERMISSIONS];

export type PermissionsResponse = {
	role: string;
	permissions: string[];
};

export const permissionsQueryKey = (practitionerId: string | undefined) => [
	'user-permissions',
	practitionerId,
];

/**
 * Imperative function to get user permissions outside of React context.
 * Uses queryClient.ensureQueryData to cache results.
 * Used by route guards in beforeLoad hooks.
 */
export async function getUserPermissions(): Promise<PermissionsResponse> {
	const profile = medplum.getProfile();
	if (!profile || profile.resourceType !== 'Practitioner') {
		return { role: '', permissions: [] };
	}

	return queryClient.ensureQueryData({
		queryKey: permissionsQueryKey(profile.id),
		queryFn: async () => {
			const bot = await queryClient.ensureQueryData({
				queryKey: ['bot', 'get-permissions'],
				queryFn: () =>
					medplum.searchOne('Bot', {
						identifier: `${CUSTOM_IDENTIFIER_SYSTEMS.BOT_IDENTIFIER}|get-permissions`,
					}),
				staleTime: Number.POSITIVE_INFINITY,
			});

			if (!bot?.id) {
				throw new Error('Get Permissions Bot not found');
			}

			return medplum.executeBot(bot.id, {
				practitionerId: profile.id,
			}) as Promise<PermissionsResponse>;
		},
	});
}

export function hasPermission(permissions: string[], permission: AppPermission): boolean {
	return permissions.includes(APP_PERMISSIONS.SUPERADMIN) || permissions.includes(permission);
}
